import type { KeycloakInstance, KeycloakLoginOptions } from "keycloak-js";
import { clearAuthCookie, setAuthCookie } from "./cookie";
import { ensureKeycloak, getKeycloak, resetKeycloakClient } from "./client";
import { getKeycloakConfig } from "./config";

type TokenSink = (token: string) => void;
type AuthLogoutHandler = () => void;

let initPromise: Promise<KeycloakInstance | null> | null = null;
let tokenSink: TokenSink | null = null;
let authLogoutHandler: AuthLogoutHandler | null = null;
const KEYCLOAK_AUTH_PARAMS = [
  "code",
  "state",
  "session_state",
  "iss",
  "error",
  "error_description",
  "error_uri",
];

const hasKeycloakAuthParamsInValue = (value: string): boolean => {
  if (!value) return false;
  return KEYCLOAK_AUTH_PARAMS.some((param) =>
    new RegExp(`(?:^|[?#&])${param}=`).test(value)
  );
};

export const hasKeycloakAuthParams = (): boolean => {
  if (typeof window === "undefined") return false;
  return (
    hasKeycloakAuthParamsInValue(window.location.search) ||
    hasKeycloakAuthParamsInValue(window.location.hash) ||
    hasKeycloakAuthParamsInValue(window.location.hash.replace(/^#/, ""))
  );
};

const stripKeycloakAuthParams = (url: URL): URL => {
  KEYCLOAK_AUTH_PARAMS.forEach((param) => {
    url.searchParams.delete(param);
  });

  const originalHash = url.hash;
  const hashValue = url.hash.replace(/^#/, "");
  if (hasKeycloakAuthParamsInValue(hashValue)) {
    const hashParams = new URLSearchParams(hashValue);
    KEYCLOAK_AUTH_PARAMS.forEach((param) => {
      hashParams.delete(param);
    });
    const cleanedHash = hashParams.toString();
    url.hash = cleanedHash ? `#${cleanedHash}` : "";
  } else {
    url.hash = originalHash;
  }

  return url;
};

const clearKeycloakAuthParamsFromUrl = (): void => {
  if (typeof window === "undefined") return;
  if (!hasKeycloakAuthParams()) return;
  const cleanedUrl = stripKeycloakAuthParams(
    new URL(window.location.href)
  ).toString();
  if (cleanedUrl !== window.location.href) {
    window.history.replaceState({}, document.title, cleanedUrl);
  }
};

const getCleanRedirectUri = (): string => {
  if (typeof window === "undefined") return "";
  return stripKeycloakAuthParams(new URL(window.location.href)).toString();
};

function pushToken(token?: string | null) {
  if (!token || !tokenSink) return;
  tokenSink(token);
}

/**
 * Register a callback to receive refreshed tokens.
 */
export function registerKeycloakTokenSink(sink: TokenSink | null): void {
  tokenSink = sink;
}

export function registerKeycloakAuthLogoutHandler(
  handler: AuthLogoutHandler | null
): void {
  authLogoutHandler = handler;
}

/**
 * Initialize Keycloak once (client-only). Returns the Keycloak instance.
 * Uses standard OIDC flow with PKCE and silent SSO check.
 */
export async function initKeycloakSession(): Promise<KeycloakInstance | null> {
  if (typeof window === "undefined") return null;
  if (initPromise) return initPromise;

  initPromise = (async () => {
    const kc = await ensureKeycloak();
    if (!kc) return null;

    const responseMode = hasKeycloakAuthParamsInValue(window.location.hash)
      ? "fragment"
      : "query";

    await kc.init({
      onLoad: "check-sso",
      pkceMethod: "S256",
      flow: "standard",
      responseMode,
      checkLoginIframe: true,
      silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
    } as any);

    clearKeycloakAuthParamsFromUrl();

    if (kc.token) {
      setAuthCookie(kc.token);
      pushToken(kc.token);
    }

    kc.onTokenExpired = async () => {
      try {
        const refreshed = await kc.updateToken(30);
        if (refreshed && kc.token) {
          setAuthCookie(kc.token);
          pushToken(kc.token);
        }
      } catch {
        clearAuthCookie();
        await kc.login({ redirectUri: window.location.href });
      }
    };

    kc.onAuthLogout = () => {
      clearAuthCookie();
      resetKeycloakSessionState();
      authLogoutHandler?.();
    };

    return kc;
  })();

  return initPromise;
}

/**
 * Get a valid access token, refreshing if needed.
 * Returns undefined if not authenticated.
 */
export async function getValidKeycloakToken(): Promise<string | undefined> {
  const kc = await initKeycloakSession();
  if (!kc || !kc.authenticated) return undefined;

  if (kc.isTokenExpired(30)) {
    try {
      await kc.updateToken(30);
    } catch {
      return undefined;
    }
  }

  if (kc.token) {
    setAuthCookie(kc.token);
    pushToken(kc.token);
  }

  return kc.token ?? undefined;
}

export function resetKeycloakSessionState(): void {
  initPromise = null;
  tokenSink = null;
  resetKeycloakClient();
}

/**
 * Standard login redirect via Keycloak.
 */
export async function keycloakLogin(options?: {
  promptLogin?: boolean;
}): Promise<void> {
  const kc = (await initKeycloakSession()) ?? getKeycloak();
  if (!kc) return;
  const loginOptions: KeycloakLoginOptions = {
    redirectUri: getCleanRedirectUri(),
  };
  if (options?.promptLogin) {
    loginOptions.prompt = "login";
    loginOptions.maxAge = 0;
  }
  await kc.login(loginOptions);
}

function buildKeycloakLogoutUrl(
  redirectUri: string,
  idTokenHint?: string
): string {
  const { url, realm, clientId } = getKeycloakConfig();
  const base = url.replace(/\/+$/, "");
  const logoutUrl = new URL(
    `${base}/realms/${encodeURIComponent(realm)}/protocol/openid-connect/logout`
  );
  logoutUrl.searchParams.set("client_id", clientId);
  logoutUrl.searchParams.set("post_logout_redirect_uri", redirectUri);
  if (idTokenHint) {
    logoutUrl.searchParams.set("id_token_hint", idTokenHint);
  }
  return logoutUrl.toString();
}

/**
 * End the Keycloak browser session via the OIDC logout endpoint.
 * Uses id_token_hint from the active keycloak-js session for reliable federated logout.
 */
export async function keycloakLogout(
  redirectUri?: string
): Promise<void> {
  if (typeof window === "undefined") return;

  const kc = getKeycloak() ?? (await initKeycloakSession());
  clearAuthCookie();

  const targetUri =
    redirectUri || `${window.location.origin}/signin`;
  const idTokenHint = kc?.idToken?.trim() || undefined;

  resetKeycloakSessionState();
  window.location.assign(buildKeycloakLogoutUrl(targetUri, idTokenHint));
}

